/*
Manages the views. Renders children if no access issues encountered.
*/

import React, { ReactNode, useEffect } from 'react';
import cn from 'classnames';
import { useDispatch } from 'react-redux';
import Container from '@components/ContentContainer';
import { setActiveContact } from '@repo/redux/actions/ui';
import { useSearchParams } from 'react-router-dom';
import { useAuthContext } from '@repo/hooks';
import ViewMessage from '@components/ViewMessage';
import { ActionGroupsIcon, CircledExclamationPointIcon, NavPeopleIcon } from '@repo/icons';
import { GenericErrorMessage } from '@views/ErrorBoundary';
import LoginOverlay from '@components/Login/Overlay';
import styles from './SharedViewWrapper.module.scss';
import VerifyEmail from '../VerifyEmail';
import {
  labelForSharedResourceType,
  SharedResourceType,
} from '@repo/redux/reducers/data/shareStatuses';
import { capitalize } from '@repo/utils/helpers/string';

const ErrorDisplay = ({ children }) => (
  <Container>
    <Container.Primary header={<></>}>{children}</Container.Primary>
    <Container.Sidebar className={cn(styles.sidebarContent)} />
  </Container>
);

const iconForSharedResourceType: Record<SharedResourceType, ReactNode> = {
  contact: <NavPeopleIcon color="textPrimary" width={18} height={18} />,
  group: <ActionGroupsIcon color="textPrimary" width={18} height={18} />,
};

export default function SharedViewWrapper({
  children,
  sharedResourceType,
  displayLogIn,
  error,
}: {
  children: ReactNode;
  sharedResourceType: SharedResourceType;
  displayLogIn: boolean;
  error?: { status: number };
}) {
  const dispatch = useDispatch();
  const { user } = useAuthContext();
  const [searchParams] = useSearchParams();
  const verifyEmailID = searchParams.get('id');
  const resourceLabel = labelForSharedResourceType[sharedResourceType];

  useEffect(() => {
    dispatch(setActiveContact(null));
  }, []);

  useEffect(() => {
    if (!user) {
      document.body.classList.add('unauthenticatedUser');
      return () => document.body.classList.remove('unauthenticatedUser');
    }
    return () => {};
  }, [user]);

  if (user && error?.status == 404) {
    return (
      <ErrorDisplay>
        <ViewMessage
          icon={iconForSharedResourceType[sharedResourceType]}
          headline={`${capitalize(resourceLabel)} not found`}
          message={`The ${resourceLabel} you requested could not be found`}
        />
      </ErrorDisplay>
    );
  }

  if (user && error?.status == 403 && verifyEmailID) {
    return (
      <ErrorDisplay>
        <VerifyEmail sharedResourceType={sharedResourceType} />
      </ErrorDisplay>
    );
  }

  if (user && error?.status == 403) {
    return (
      <ErrorDisplay>
        <ViewMessage
          icon={<CircledExclamationPointIcon />}
          headline="Access Denied"
          message="You do not have permission to access this page"
        />
      </ErrorDisplay>
    );
  }

  if (user && !!error) {
    return (
      <ErrorDisplay>
        <GenericErrorMessage />
      </ErrorDisplay>
    );
  }

  if (displayLogIn || (!user && !!error)) {
    return <LoginOverlay />;
  }

  return <>{children}</>;
}
